import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, m } from "motion/react";

import { api, ApiError } from "../lib/api.js";
import { deviceKey } from "../lib/device.js";
import { PoweredBy } from "../components/PoweredBy.js";
import { VoiceNoteRecorder } from "../components/VoiceNoteRecorder.js";
import { prepareMedia, uploadThumbnail } from "../lib/derivatives.js";
import { PHOTO_FILTERS, applyFilter, cssFilterFor } from "../lib/photobooth.js";
import { guestErrorMessage, useGuestLocale, type GuestCopy } from "../lib/i18n.js";
import { LanguagePicker } from "../components/LanguagePicker.js";
import { runPool } from "../lib/upload-pool.js";
import { springs } from "../lib/motion.js";
import type { Event, GalleryAlbum, Media } from "@shared";
import { LIMITS } from "@shared";

type ItemStatus = "pending" | "uploading" | "done" | "failed";

interface QueueItem {
  id: string;
  file: File;
  status: ItemStatus;
  progress: number;
  /**
   * The guest's words about this one file.
   *
   * It lives on the queue item rather than in a map keyed by filename so a
   * retry or a batch run carries it through unchanged. It is sent only in
   * `upload-complete`; nothing reaches the network before Upload is pressed.
   */
  caption: string;
  /**
   * The photo-booth preset this file gets when it is sent.
   *
   * Chosen per file, because "make this one sepia" is a real request and
   * "make everything sepia" is a mood. Applied in the browser on the decoded
   * JPEG after HEIC conversion, so a filter never depends on the browser being
   * able to decode HEIC directly — see photobooth.ts for why `ctx.filter` has
   * a manual pixel fallback.
   */
  filter: string;
  error?: string;
  previewUrl?: string;
  /** Attempts made in the current page. Drives the per-row Retry button. */
  attempts: number;
}

const MAX_BYTES_FREE = 25 * 1024 * 1024;
const ALLOWED_FILE_TYPE =
  /^(image\/(jpeg|png|webp|gif|hei[cf])|video\/(mp4|quicktime|webm)|audio\/(webm|ogg|mp4|mpeg|wav))$/;

/**
 * How many files send at once.
 *
 * One network file at a time leaves the phone idle while a photograph waiting
 * behind it could already be sending; full parallelism would starve each
 * connection on venue Wi-Fi and thrash the radio. Three is the point at which
 * each upload's client-side preparation — decode, thumbnail — is fully hidden
 * behind the previous file's network phase.
 */
const UPLOAD_CONCURRENCY = 3;

export function GuestUpload() {
  const { guestCode } = useParams<{ guestCode: string }>();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [queue, setQueueState] = useState<QueueItem[]>([]);
  const [running, setRunning] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [dragging, setDragging] = useState(false);
  // The host's albums, and the one this batch files into. "" is the event's
  // main collection, which is also what a guest gets when there are no albums.
  const [albums, setAlbums] = useState<GalleryAlbum[]>([]);
  const [albumId, setAlbumId] = useState("");
  // The event's guest language drives these words: a Spanish event shows the
  // Spanish composer even on an English phone. See lib/i18n.ts for the fallback
  // chain when the event has no fixed language.
  const locale = useGuestLocale(event?.guestLanguage ?? "");
  const { copy } = locale;
  // What this browser sent. Kept so a guest can take a photo back afterwards
  // without the server having to trust anything they type now.
  const [mine, setMine] = useState<Media[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  /*
   * Confetti, once, the moment the whole batch has landed. This is the
   * moment the guest came for, and it should feel like it. Loaded only when
   * needed (it is not part of the page), skipped under reduced motion, and
   * fired again only after another batch completes.
   */
  const batchComplete = queue.length > 0 && queue.every((q) => q.status === "done");
  const celebrated = useRef(false);
  useEffect(() => {
    if (!batchComplete) {
      celebrated.current = false;
      return;
    }
    if (celebrated.current) return;
    celebrated.current = true;
    void import("canvas-confetti").then(({ default: confetti }) => {
      const colors = ["#ef3f7a", "#b44bec", "#ffb4d0", "#ffd166", "#ffffff"];
      const base = { disableForReducedMotion: true, colors, ticks: 220, scalar: 0.9, zIndex: 60 };
      confetti({ ...base, particleCount: 90, spread: 75, startVelocity: 42, origin: { x: 0.5, y: 0.75 } });
      // Two side bursts a beat later, like party poppers from either side.
      setTimeout(() => {
        confetti({ ...base, particleCount: 45, angle: 60, spread: 55, origin: { x: 0, y: 0.8 } });
        confetti({ ...base, particleCount: 45, angle: 120, spread: 55, origin: { x: 1, y: 0.8 } });
      }, 180);
    });
  }, [batchComplete]);

  /**
   * The queue, readable synchronously.
   *
   * `uploadAll` needs to see the *current* list between items, not the one that
   * existed when the button was pressed: an item added halfway through a run has
   * to be picked up, and an item that failed has to be recognised as failed. A
   * ref that mirrors state gives both without making the upload loop re-render
   * itself into existence.
   */
  const queueRef = useRef<QueueItem[]>([]);
  const setQueue = useCallback(
    (next: QueueItem[] | ((prev: QueueItem[]) => QueueItem[])) => {
      queueRef.current = typeof next === "function" ? next(queueRef.current) : next;
      setQueueState(queueRef.current);
    },
    [],
  );

  /** Stops a second Upload press from starting a second pass over the same rows. */
  const runningRef = useRef(false);

  /**
   * Two inputs, deliberately.
   *
   * `capture` on the multi-upload input forced the rear camera open on iOS
   * Safari and Android Chrome and frequently disabled the photo library picker
   * along with it. That is the wrong trade for this product: most guests have
   * already taken their photos, and the entire point is to collect the ones from
   * earlier in the evening. A guest whose only control was a camera shutter
   * would have gone back to the camera roll and sent nothing.
   *
   * So: one input for taking a picture now, one for choosing what is already
   * there. Both feed the same queue.
   */

  useEffect(() => {
    if (!guestCode) return;

    api
      .get<{ event: Event }>(`/api/events/lookup/${guestCode}`)
      .then((result) => setEvent(result.event))
      .catch((err) => {
        setLoadError(
          err instanceof ApiError && err.status === 404
            ? copy.linkDoesNotExist
            : guestErrorMessage(copy, err),
        );
      })
      .finally(() => setLoading(false));
  }, [guestCode]);

  // Album names only matter once there is somewhere to file a photo. A failure
  // here hides the picker; it must never stand between a guest and an upload.
  useEffect(() => {
    if (!event?.slug || !guestCode) return;
    api
      .get<{ albums: GalleryAlbum[] }>(`/api/events/${event.slug}/albums?guestCode=${encodeURIComponent(guestCode)}`)
      .then((result) => setAlbums(result.albums))
      .catch(() => setAlbums([]));
  }, [event?.slug, guestCode]);

  // The uploads this browser has made, so the guest can undo a mistake. Only
  // rows the server matched back to our device key come back — a device key
  // that does not match returns nothing, so two guests on two phones never see
  // each other's photos.
  const loadMine = useCallback(async () => {
    if (!guestCode) return;
    try {
      const result = await api.get<{ media: Media[] }>(
        `/api/upload/mine?guestCode=${encodeURIComponent(guestCode)}&likerKey=${encodeURIComponent(
          deviceKey(),
        )}`,
      );
      setMine(result.media);
    } catch {
      // Not being able to list is not a reason to block uploading.
    }
  }, [guestCode]);

  useEffect(() => {
    void loadMine();
  }, [loadMine]);

  const removeMine = async (mediaId: string): Promise<void> => {
    const previous = mine;
    setMine((current) => current.filter((item) => item.mediaId !== mediaId));
    try {
      await api.del(`/api/media/${mediaId}`, { guestCode, likerKey: deviceKey() });
    } catch (err) {
      setMine(previous);
      setLoadError(guestErrorMessage(copy, err));
    }
  };

  const addFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;

      const next: QueueItem[] = [];
      for (const file of Array.from(fileList)) {
        if (!ALLOWED_FILE_TYPE.test(file.type)) {
          next.push({
            id: crypto.randomUUID(),
            file,
            status: "failed",
            progress: 0,
            caption: "",
            // A refused file never reaches the filter step: it has no
            // transformation to carry, only a reason.
            filter: "",
            attempts: 1,
            error: copy.fileTypeRefused,
          });
          continue;
        }
        if (file.size > MAX_BYTES_FREE) {
          next.push({
            id: crypto.randomUUID(),
            file,
            status: "failed",
            progress: 0,
            caption: "",
            filter: "",
            attempts: 1,
            error: copy.fileTooLarge,
          });
          continue;
        }
        next.push({
          id: crypto.randomUUID(),
          file,
          status: "pending",
          progress: 0,
          // Deliberately empty per file: a batch of forty photos sharing one
          // caption is worse than no caption at all.
          caption: "",
          filter: "none",
          attempts: 0,
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
        });
      }
      setQueue((prev) => [...prev, ...next]);
    },
    [setQueue, copy],
  );

  /**
   * One file through its whole pipeline. Resolves true only when it finished.
   *
   * The "sent from this phone" list is deliberately not refetched here: with
   * three files sending at once, one refetch per completion is three refetches
   * of the same list at once. The batch runner refreshes it once when anything
   * succeeded, and the per-row Retry button covers the single-file path.
   */
  async function uploadItem(item: QueueItem): Promise<boolean> {
    const patch = (changes: Partial<QueueItem>): void =>
      setQueue((prev) => prev.map((q) => (q.id === item.id ? { ...q, ...changes } : q)));

    patch({ status: "uploading", progress: 1, error: undefined, attempts: item.attempts + 1 });

    try {
      // Dimensions, duration and the thumbnail come from the same decode pass,
      // and HEIC is converted to JPEG before it ever leaves the phone — except
      // on Safari, which decodes HEIC natively and uploads the original file
      // without touching the WASM converter. See web/src/lib/upload-prepare.ts.
      let prepared = await prepareMedia(item.file);

      // Photo-booth filter, applied to the *converted* file so the path cannot
      // depend on the browser decoding HEIC. It reruns the prepare pass so the
      // thumbnail shows the same pixels the wall will show — sending a filtered
      // original under a sepia thumbnail would make the wall lie about its own
      // photographs. A filter failure falls back to the unfiltered photo,
      // because losing the photograph to lose a mood is the wrong trade.
      if (item.filter !== "none" && prepared.file.type.startsWith("image/")) {
        try {
          const filtered = await applyFilter(prepared.file, item.filter);
          const next =
            filtered instanceof File
              ? filtered
              : new File([filtered], item.file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
          prepared = await prepareMedia(next);
        } catch {
          // Keep the unfiltered prepared file.
        }
      }

      const ticket = await api.post<{
        ticketId: string;
        uploadUrl: string;
        mode: "direct" | "proxy";
      }>("/api/upload-ticket", {
        guestCode,
        filename: prepared.file.name,
        mimeType: prepared.file.type || "application/octet-stream",
        sizeBytes: prepared.file.size,
        width: prepared.width,
        height: prepared.height,
        durationSec: prepared.durationSec,
        // The upload quota is per device, not per guest link.
        likerKey: deviceKey(),
      });

      await putWithProgress(ticket.uploadUrl, prepared.file, ticket.mode === "proxy", (progress) =>
        patch({ progress }),
      );

      // Best effort. A failed thumbnail costs the wall some bandwidth; it must
      // not cost the guest their photograph. `upload-complete` is the authority
      // on whether a valid derivative exists (it HEADs the object).
      if (prepared.thumb && event?.slug) {
        patch({ progress: 99 });
        await uploadThumbnail(ticket.ticketId, event.slug, prepared.thumb).catch(() => undefined);
      }

      await api.post<{ media: Media }>("/api/upload-complete", {
        ticketId: ticket.ticketId,
        slug: event?.slug,
        guestName: guestName.trim() || undefined,
        // The guest's caption belongs to the row, so a retry sends the same
        // words rather than the empty file alone.
        caption: item.caption.trim() || undefined,
        // Read at send time, so a retry files into the album the batch chose.
        albumId: albumId || undefined,
        // Lets this browser take the upload back later without the host.
        likerKey: deviceKey(),
      });

      patch({ status: "done", progress: 100 });
      // The hero pill counts the album; a guest who just added two photos should
      // see it move without reloading the page.
      setEvent((prev) => (prev ? { ...prev, mediaCount: prev.mediaCount + 1 } : prev));
      return true;
    } catch (err) {
      // Reached for every failure now that putWithProgress settles on all of
      // them, which is what puts this row back into a retryable state instead of
      // leaving it stuck at "uploading" for the life of the page.
      patch({ status: "failed", error: describeUploadError(err) });
      return false;
    }
  }

  /**
   * Why an upload failed, in words the guest can use.
   *
   * The storage ceiling is the one failure here that is not the guest's fault and
   * not fixable by them retrying, so the server's phrasing is replaced with
   * something that says what to do next. Without this the guest sees "The host
   * is out of storage" on a phone at a table, taps retry out of habit, and fails
   * identically nine more times while the host has no idea anyone is trying.
   */
  function describeUploadError(error: unknown): string {
    if (error instanceof UploadTransportError) return transportMessage(copy, error.kind);
    return guestErrorMessage(copy, error);
  }

  /**
   * Send everything outstanding, three files at a time.
   *
   * Re-reads the queue each pull instead of iterating the snapshot taken when
   * the button was pressed, and refuses to start a second pass. All three were
   * real:
   *
   *   - the snapshot version skipped any file that failed mid-run, because it
   *     had already moved past that index, so a guest with ten photos and one
   *     bad network moment got nine uploads and no error on the tenth
   *   - with no guard, a double-tap on the phone this page exists for ran two
   *     passes over the same rows and put two copies of every photograph on the
   *     host's wall
   *   - one file at a time left the phone idle while the next photograph
   *     waited; three concurrent uploads hide each file's preparation behind
   *     the previous file's network phase. The scheduling semantics — max
   *     in flight, one attempt per item per press, failures never starve the
   *     queue, full drain — are pinned by scripts/verify/verify-upload-pool.ts.
   *
   * Each row is attempted at most once per press. Retrying automatically
   * inside the same press would hide a genuine failure behind a spinner and
   * burn the guest's per-event quota on attempts nobody asked for; the Retry
   * button below is where a retry belongs.
   */
  async function uploadAll(): Promise<void> {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);

    const attempted = new Set<string>();
    let anyDone = false;

    try {
      await runPool(
        UPLOAD_CONCURRENCY,
        () => {
          const next = queueRef.current.find(
            (q) => !attempted.has(q.id) && (q.status === "pending" || q.status === "failed"),
          );
          if (!next) return null;
          attempted.add(next.id);
          return next;
        },
        async (item) => {
          if (await uploadItem(item)) anyDone = true;
        },
        (error, item) => {
          // uploadItem settles its own row, so reaching this is a defect, not
          // an upload failure — say so in the console rather than swallowing it.
          console.error("Upload worker crashed on item", item.id, error);
        },
      );
    } finally {
      runningRef.current = false;
      setRunning(false);
      if (anyDone) void loadMine();
    }
  }

  if (loading) {
    return (
      <div className="guest">
        <header className="guest-top">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
        </header>
        <div className="card empty-state">
          <span className="empty-icon" aria-hidden="true">
            <ImageIcon />
          </span>
          <h1 style={{ fontSize: "1.5rem" }}>{copy.openingAlbum}</h1>
          <p className="muted">{copy.oneMoment}</p>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="guest">
        <header className="guest-top">
          <span className="brand">
            <span className="brand-mark" aria-hidden="true" />
            Eventgram
          </span>
        </header>
        <div className="card empty-state">
          <span className="empty-icon" aria-hidden="true">
            🔗
          </span>
          <h1 style={{ fontSize: "1.5rem" }}>{copy.linkNotFound}</h1>
          <p className="muted">{loadError || copy.linkDoesNotExist}</p>
        </div>
      </div>
    );
  }

  const done = queue.filter((q) => q.status === "done").length;
  // A row in flight counts as active so the queue cannot be cleared underneath an
  // upload. A row that failed does not: it is exactly what the button below is for.
  const uploading = queue.some((q) => q.status === "uploading");
  const outstanding = queue.filter((q) => q.status === "pending" || q.status === "failed").length;
  // How far through the whole batch this send is, for the fill inside Send.
  const overall = queue.length
    ? Math.round(
        queue.reduce((sum, q) => sum + (q.status === "done" ? 100 : q.status === "uploading" ? q.progress : 0), 0) /
          queue.length,
      )
    : 0;
  const allSent = done > 0 && done === queue.length;

  return (
    <div className="guest">
      <header className="guest-top">
        <span className="brand">
          <span className="brand-mark" aria-hidden="true" />
          Eventgram
        </span>
        <span className="guest-top-tools">
          <LanguagePicker locale={locale} />
          <span className="guest-badge">{copy.privateAlbum}</span>
        </span>
      </header>

      <div className="guest-hero">
        <span className="guest-hero-glow" aria-hidden="true" />
        <p className="eyebrow">{copy.uploadTitle}</p>
        <h1>{event.title}</h1>
        <p className="guest-hero-copy">{copy.uploadHint}</p>
        <div className="guest-hero-meta">
          <span className="guest-chip">
            {event.mediaCount === 0 ? copy.firstPhoto : locale.plural("photosInAlbum", event.mediaCount)}
          </span>
          {event.eventDate ? (
            <span className="guest-chip">{locale.date(event.eventDate)}</span>
          ) : null}
        </div>
      </div>

      <div className="card guest-section">
        <div className="guest-name">
          {/* The label already says "(optional)" in every language; a second
              "optional" tag beside it said the same thing twice. */}
          <label htmlFor="guestName">
            <span>{copy.namePlaceholder}</span>
          </label>
          <input
            id="guestName"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            autoComplete="name"
            placeholder={copy.nameExample}
            style={{ width: "100%" }}
          />
        </div>

        <div
          className={`dropzone${dragging ? " dropzone-active" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          <span className="dropzone-icon" aria-hidden="true">
            <CameraIcon />
          </span>
          <h2>{copy.uploadTitle}</h2>
          <p className="muted">{copy.uploadHint}</p>

          <div className="dropzone-actions">
            <button type="button" onClick={() => inputRef.current?.click()}>
              <ImageIcon />
              {copy.chooseFiles}
            </button>
            <button
              type="button"
              className="button button-secondary"
              onClick={() => cameraRef.current?.click()}
            >
              <CameraIcon />
              {copy.camera}
            </button>
          </div>

          {/* Library: multiple, and no `capture`, so the picker stays open. */}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {/* Camera: single, rear-facing, and separate so it cannot hijack the picker. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />

          <button
            type="button"
            className="lp-btn lp-btn-ghost"
            style={{ marginTop: "0.5rem" }}
            onClick={() => setRecorderOpen(true)}
          >
            Record a voice note
          </button>
          <p className="dropzone-hint">{copy.uploadLimitHint}</p>
        </div>

        {queue.length > 0 && !(done > 0 && done === queue.length) ? (
          <div className="upload-panel">
            <div className="upload-panel-head">
              <strong>{copy.sendQueue}</strong>
              <span className="muted" style={{ fontSize: "0.8rem" }} aria-live="polite">
                {locale.t("sentProgress", { done, total: queue.length })}
              </span>
            </div>

            {albums.length > 0 ? (
              <label className="upload-album">
                <span>{copy.albumLabel}</span>
                <select
                  value={albumId}
                  onChange={(e) => setAlbumId(e.target.value)}
                  disabled={running}
                  style={{ fontSize: "16px" }}
                >
                  <option value="">{copy.mainAlbum}</option>
                  {albums.map((entry) => (
                    <option key={entry.albumId} value={entry.albumId}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <ul className="upload-list">
              {/* Picked photos glide in; a removed one folds away and the list closes up. */}
              <AnimatePresence initial={false}>
              {queue.map((item, index) => (
                <m.li
                  key={item.id}
                  className="upload-row"
                  layout="position"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0, transition: { ...springs.soft, delay: Math.min(index, 6) * 0.04 } }}
                  exit={{ opacity: 0, x: 24, height: 0, paddingTop: 0, paddingBottom: 0, marginTop: 0, transition: { duration: 0.22 } }}
                >
                  <span className="upload-thumb-wrap">
                    {item.previewUrl ? (
                      // Same CSS string the chip uses, so the queue thumbnail is
                      // already wearing the chosen look before it is sent.
                      <img
                        className="upload-thumb"
                        src={item.previewUrl}
                        alt=""
                        style={{ filter: cssFilterFor(item.filter) }}
                      />
                    ) : (
                      <span className="upload-thumb" aria-hidden="true">
                        {item.file.type.startsWith("video/") ? "▶" : "▧"}
                      </span>
                    )}
                    {/* A tick lands on the photo the moment it is safely sent. */}
                    <AnimatePresence>
                      {item.status === "done" ? (
                        <m.span
                          className="upload-thumb-done"
                          aria-hidden="true"
                          initial={{ scale: 0, rotate: -30 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={springs.bouncy}
                        >
                          ✓
                        </m.span>
                      ) : null}
                    </AnimatePresence>
                  </span>

                  <div className="upload-main">
                    <div className="upload-top">
                      <span className="upload-name" title={item.file.name}>
                        {item.file.name}
                      </span>
                      <span className={`upload-badge upload-badge-${item.status}`}>
                        {statusLabel(copy, item.status)}
                      </span>
                    </div>

                    <div className="upload-caption">
                      <input
                        type="text"
                        value={item.caption}
                        onChange={(e) =>
                          setQueue((prev) =>
                            prev.map((q) =>
                              q.id === item.id ? { ...q, caption: e.target.value } : q,
                            ),
                          )
                        }
                        maxLength={LIMITS.maxCaptionChars}
                        placeholder={copy.captionPlaceholder}
                        aria-label={locale.t("captionFor", { name: item.file.name })}
                        // Editing after the upload-complete request has gone is
                        // either lost or a second request; this session sends
                        // captions with the upload, so a sent row is read-only.
                        disabled={item.status === "uploading" || item.status === "done"}
                      />
                      {item.caption.length > 400 ? (
                        <span className="upload-caption-count" aria-hidden="true">
                          {item.caption.length} / {LIMITS.maxCaptionChars}
                        </span>
                      ) : null}
                    </div>

                    {/*
                      Photo-booth chips, one row per photograph. Both the chip's
                      swatch and the queue thumbnail carry the same CSS string,
                      so what the guest sees while choosing is what will land on
                      the wall — the preview-is-the-fact rule.
                    */}
                    {item.status === "pending" && item.file.type.startsWith("image/") ? (
                      <div className="upload-filters stack" style={{ gap: "0.25rem", marginTop: "0.4rem" }}>
                        <span className="muted" style={{ fontSize: "0.72rem" }}>
                          {copy.filterLabel}
                        </span>
                        <div className="filter-chips">
                          {PHOTO_FILTERS.map((filter) => (
                            <button
                              key={filter.id}
                              type="button"
                              className="filter-chip"
                              aria-pressed={item.filter === filter.id}
                              onClick={() =>
                                setQueue((prev) =>
                                  prev.map((q) =>
                                    q.id === item.id ? { ...q, filter: filter.id } : q,
                                  ),
                                )
                              }
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: "1.1rem",
                                  height: "1.1rem",
                                  borderRadius: "50%",
                                  background: "linear-gradient(135deg, #ff9a5a, #3ab7c6, #234e70)",
                                  filter: cssFilterFor(filter.id),
                                  display: "inline-block",
                                }}
                              />
                              {filter.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {item.status === "uploading" ? (
                      <div
                        className="progress-track"
                        role="progressbar"
                        aria-label={locale.t("uploadingFile", { name: item.file.name })}
                        aria-valuenow={item.progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div className="progress-fill" style={{ width: `${item.progress}%` }} />
                      </div>
                    ) : null}

                    {item.error ? (
                      <p className="error" style={{ margin: 0, fontSize: "0.78rem" }}>
                        {item.error}
                      </p>
                    ) : null}

                    {/*
                      Granular retry. "Upload" re-sends the whole outstanding queue,
                      which on venue wifi means re-sending nine files that already
                      worked to fix the one that did not — and on the free tier each
                      attempt costs the guest a slot of their fifty. One row, one
                      button, for the file that actually failed.
                    */}
                    {item.status === "failed" ? (
                      <div className="row" style={{ gap: "0.6rem" }}>
                        <button
                          type="button"
                          className="link-button"
                          disabled={running}
                          onClick={() => {
                            void uploadItem(item).then((ok) => {
                              if (ok) void loadMine();
                            });
                          }}
                        >
                          {copy.retryFile}
                        </button>
                        {item.attempts > 1 ? (
                          <span className="muted" style={{ fontSize: "0.72rem" }}>
                            {locale.t("attempts", { n: item.attempts })}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="link-button danger"
                          onClick={() => setQueue((prev) => prev.filter((q) => q.id !== item.id))}
                        >
                          {copy.remove}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </m.li>
              ))}
              </AnimatePresence>
            </ul>

            {/*
              Sticky on a phone so the one action that finishes the job is
              reachable after a long camera roll, without a scroll back up.
              Once nothing is outstanding the bar has no job left, so it gives
              way to the "All sent" note instead of showing a dead Upload.
            */}
            {running || outstanding > 0 ? (
              <div className="upload-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setQueue([])}
                  disabled={running || uploading}
                >
                  {copy.clear}
                </button>
                <button
                  type="button"
                  className={`send-button${running ? " send-button-running" : ""}`}
                  onClick={() => void uploadAll()}
                  disabled={running || outstanding === 0}
                >
                  {/* The button fills as the batch goes up, so progress is where the thumb already is. */}
                  {running ? (
                    <span className="send-progress" aria-hidden="true" style={{ transform: `scaleX(${overall / 100})` }} />
                  ) : null}
                  <span className="send-label">
                    {running
                      ? `${copy.uploading} ${overall}%`
                      : `${copy.sendQueue}${outstanding > 0 ? ` (${outstanding})` : ""}`}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {allSent ? (
          <m.div
            className="guest-success"
            role="status"
            initial={{ opacity: 0, scale: 0.9, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={springs.bouncy}
          >
            <span className="guest-success-icon" aria-hidden="true">
              🎉
            </span>
            <div>
              <strong>{copy.addedToGallery}</strong>
              <span className="guest-success-copy">
                {copy.addedToGalleryDetail}
              </span>
            </div>
          </m.div>
        ) : null}
        {/*
          What this phone has sent, with a way to take one back. Sending the
          wrong photograph is the most common thing that goes wrong here, and
          "ask the host" is not an answer when the host is two hundred photos
          deep and busy.
        */}
        {mine.length > 0 ? (
          <div className="sent-section">
            <div className="upload-panel-head">
              <strong>{copy.sentFromPhone}</strong>
              <span className="muted" style={{ fontSize: "0.8rem" }}>
                {mine.length}
              </span>
            </div>

            <ul className="sent-list">
              {mine.map((item) => (
                <li key={item.mediaId} className="upload-row">
                  {/*
                    The thumbnail is loaded only once the upload is actually visible.
                    `GET /api/media/...` streams an approved row, or one the owning
                    host may see — anything else 404s, which is the point of it. A
                    pending or rejected upload therefore has no fetchable URL for the
                    guest who sent it, and asking for one puts a broken image icon on
                    the one screen where the guest is already anxious about whether
                    their photo arrived.

                    The words beside it carry the information that matters anyway,
                    and saying so is the whole job of this list: "sent, waiting for
                    the host" is a complete answer. "Sent, waiting" next to a broken
                    image is not.
                  */}
                  {item.status === "approved" ? (
                    <img
                      src={item.thumbUrl || item.displayUrl}
                      alt=""
                      className="upload-thumb"
                      loading="lazy"
                    />
                  ) : (
                    <span className="upload-thumb" aria-hidden="true">
                      {item.status === "rejected" ? "×" : "…"}
                    </span>
                  )}
                  <div className="sent-main">
                    <span className="sent-title">{item.caption || copy.photo}</span>
                    <span className="sent-sub">
                      {item.status === "pending"
                        ? copy.waitingApproval
                        : item.status === "rejected"
                          ? copy.notOnWall
                          : copy.onWall}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary sent-remove"
                    onClick={() => void removeMine(item.mediaId)}
                  >
                    {copy.remove}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {recorderOpen ? (
        <VoiceNoteRecorder
          onRecorded={(file) => {
            // Reuse the same queue pipeline as picked files.
            const item: QueueItem = {
              id: crypto.randomUUID(),
              file,
              status: "pending",
              progress: 0,
              caption: "",
              filter: "none",
              attempts: 0,
              previewUrl: undefined,
            };
            setQueueState((prev) => [...prev, item]);
          }}
          onClose={() => setRecorderOpen(false)}
        />
      ) : null}
      <PoweredBy text={copy.footer} source="guest-upload" />
    </div>
  );
}

/** The word a guest sees on a row, in their language, rather than the enum the code uses. */
function statusLabel(copy: GuestCopy, status: ItemStatus): string {
  switch (status) {
    case "pending":
      return copy.statusReady;
    case "uploading":
      return copy.statusSending;
    case "done":
      return copy.statusSent;
    default:
      return copy.statusFailed;
  }
}

/** A failure of the PUT itself, before the API ever answered: said in the guest's words. */
class UploadTransportError extends Error {
  constructor(readonly kind: "status" | "network" | "timeout" | "abort", message: string) {
    super(message);
    this.name = "UploadTransportError";
  }
}

function transportMessage(copy: GuestCopy, kind: UploadTransportError["kind"]): string {
  if (kind === "network") return copy.uploadNetworkError;
  if (kind === "timeout") return copy.uploadStalled;
  if (kind === "abort") return copy.uploadCancelled;
  return copy.uploadFailed;
}

function ImageIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m4.5 17 4.3-4.2a2 2 0 0 1 2.8 0l2.2 2.1a2 2 0 0 0 2.8 0l2.9-2.7" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 8.5A2.5 2.5 0 0 1 7 6h1.2l1.1-2h5.4l1.1 2H17a2.5 2.5 0 0 1 2.5 2.5v8A2.5 2.5 0 0 1 17 19H7a2.5 2.5 0 0 1-2.5-2.5z" />
      <circle cx="12" cy="12.5" r="3.1" />
    </svg>
  );
}

/**
 * PUT the bytes, reporting progress.
 *
 * fetch() cannot report upload progress, so XHR is used. For a direct R2 upload the
 * request is cross-origin and needs an event listener; for the proxy path it is
 * same-origin but still benefits from progress reporting.
 *
 * Every terminal outcome is handled, and that is the fix rather than a detail.
 * The previous version listened only for `load` and `error`, which left `abort`
 * and `timeout` unhandled — and neither of those fires a promise rejection on its
 * own. A single upload that stalled long enough for the connection to be torn down
 * therefore never settled: its promise stayed pending forever, `uploadItem` never
 * reached the line that marks the item failed, and the row sat at "uploading" for
 * the rest of the page's life. `active` was therefore permanently true, which
 * disabled the Upload button, which is the only control that could ever clear it.
 *
 * That is the deadlock: not a slow upload, but an upload that could not be
 * retried, abandoned, or acknowledged, on a page whose entire job is uploading.
 */
function putWithProgress(
  url: string,
  file: File,
  isProxy: boolean,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);

    // A venue's wifi will hold a connection open indefinitely rather than
    // refusing it. Without a deadline a 4 MB video can sit at 12% for ten
    // minutes and the guest has no way to tell that from progress.
    xhr.timeout = Math.max(60_000, file.size / 20_000);

    if (!isProxy) {
      // Required for the signed R2 request; harmless on our own proxy route.
      xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    }

    // Settle exactly once. Every one of these can fire, and several of them can
    // fire for the same failure — `timeout` then `abort`, or `load` after the
    // connection dropped mid-body — and a second resolve/reject on an already
    // settled promise is at best a confusing unhandled rejection in the console
    // on the one screen a guest is watching for errors.
    let settled = false;
    const finish = (outcome: () => void): void => {
      if (settled) return;
      settled = true;
      outcome();
    };

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      finish(() => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new UploadTransportError("status", `Upload failed (${xhr.status})`));
      });
    };
    xhr.onerror = () => finish(() => reject(new UploadTransportError("network", "Network error during upload.")));
    xhr.ontimeout = () => finish(() => reject(new UploadTransportError("timeout", "The upload stopped responding.")));
    xhr.onabort = () => finish(() => reject(new UploadTransportError("abort", "Upload cancelled.")));

    xhr.send(file);
  });
}
